import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { LinkProps } from '@coursera/cds-core';
import { Link } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import SimpleRichTextContentful from 'bundles/coursera-plus-landing-page/components/SimpleRichTextContentful';
import type { RichTextObject } from 'bundles/coursera-plus-landing-page/components/SimpleRichTextContentful';
import { constants, numberOfProducts, numberOfProductsNoPlusSign } from 'bundles/coursera-plus/utils/courseraPlusUtils';

import _t from 'i18n!nls/enroll';

const BASE_TRACKING_NAME = 'faq';

type FAQContent = { question: string; answer: string | JSX.Element };
export type FAQContentList = Array<FAQContent>;

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

const getExistingSubscriptions = (removeLinks = false): FAQContent => {
  const cancelYourExistingSubscriptionsText = _t('cancel your existing subscriptions');

  return {
    question: _t(
      'What happens to my existing Coursera Specialization subscriptions when I subscribe to Coursera Plus?'
    ),
    answer: (
      <div>
        <FormattedMessage
          message={_t(
            `If you have existing Coursera Specialization subscriptions, they won't automatically cancel when you start a Coursera Plus subscription. You must {cancelYourExistingSubscriptions} to avoid being charged for both Coursera Plus and single Specialization subscriptions. Your course progress will be saved and won't be impacted by canceling previous subscription(s).`
          )}
          cancelYourExistingSubscriptions={
            removeLinks ? (
              cancelYourExistingSubscriptionsText
            ) : (
              <TrackedLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://learner.coursera.help/hc/articles/216348123"
                trackingName={`${BASE_TRACKING_NAME}_cancel_existing_subs_link`}
              >
                {cancelYourExistingSubscriptionsText}
              </TrackedLink>
            )
          }
        />
      </div>
    ),
  };
};

const getExistingSubscriptionsForLite = (removeLinks = false): FAQContent => {
  const cancelYourExistingSubscriptionsText = _t('cancel your existing subscriptions');
  const cPlusText = _t('Coursera Plus');

  return {
    question: _t('What will happen to my existing Specialization subscriptions when I subscribe to Coursera Lite?'),
    answer: (
      <div>
        <FormattedMessage
          message={_t(
            `Your existing Specialization subscriptions won't automatically cancel when you start a Coursera Lite subscription. You'll need to {cancelYourExistingSubscriptions} to avoid being charged. Coursera Lite only offers access to videos and lectures. To save your progress for assessments and quizzes, upgrade to {courseraPlus}.`
          )}
          cancelYourExistingSubscriptions={
            removeLinks ? (
              cancelYourExistingSubscriptionsText
            ) : (
              <TrackedLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://learner.coursera.help/hc/articles/216348123"
                trackingName={`${BASE_TRACKING_NAME}_cancel_existing_subs_link`}
              >
                {cancelYourExistingSubscriptionsText}
              </TrackedLink>
            )
          }
          courseraPlus={
            removeLinks ? (
              cPlusText
            ) : (
              <TrackedLink
                target="_blank"
                rel="noopener noreferrer"
                href="/courseraplus"
                trackingName={`${BASE_TRACKING_NAME}_coursera_plus_link`}
              >
                {cPlusText}
              </TrackedLink>
            )
          }
        />
      </div>
    ),
  };
};

const getReimbursedByEmployer = () => ({
  question: _t('Can I get reimbursed by my employer for Coursera Plus?'),
  answer: (
    <div>
      {_t(
        `Yes, if your employer offers educational reimbursements that include online learning programs. You'll receive an email with your Coursera Plus receipt to submit.`
      )}
    </div>
  ),
});

const getReimbursedByEmployerForLite = () => ({
  question: _t('Can I get reimbursed by my employer for a Coursera Lite subscription?'),
  answer: (
    <div>
      {_t(
        "Yes, if your employer offers educational reimbursements that cover online learning programs. We'll send you a Coursera Lite confirmation email with your receipt."
      )}
    </div>
  ),
});

const ShowContentfulFaq: React.LegacyFunctionComponentWithChildren<{}> = (props) => {
  return <div className="m-t-1">{props.children}</div>;
};

const getTryCourseraPlusFirst = (explanationForFaq?: RichTextObject | null) => {
  const contentfulAnswer = explanationForFaq ? (
    <div style={{ marginTop: '-15px' }}>
      {/* the arbitrary looking styling takes away the top margin of the first paragraph */}
      <SimpleRichTextContentful richText={explanationForFaq} component={ShowContentfulFaq} />
    </div>
  ) : undefined;

  const myPurchasesPageText = _t('My Purchases page');
  const defaultAnswer = (
    <div>
      <div>{_t('Yes! We have two options, depending on which payment plan you choose.')}</div>
      <div className="m-t-1">
        {_t(
          "If you opt to make monthly payments, you can take advantage of a 7-day free trial to experience learning with Coursera Plus before you decide to purchase. We do capture your payment information when you subscribe, but you won't be charged until the end of your 7-day free trial. So, if you decide Coursera Plus isn't right for you, just cancel during the trial period and there will be no charge."
        )}
      </div>
      <div className="m-t-1">
        <FormattedMessage
          message={_t(
            `If you opt to make an annual payment, you'll have up to 14 days to submit a refund request if you decide that Coursera Plus isn't right for you. The process is simple—just go to your {myPurchasesPage} within 14 days of your payment and submit a refund request. No additional information is required. Please note that any certificates that you earn within your first 14 days will be revoked if you decide to get a refund within that period. `
          )}
          myPurchasesPage={
            <TrackedLink
              target="_blank"
              rel="noopener noreferrer"
              href={constants.MY_PURCHASES_LINK}
              trackingName={`${BASE_TRACKING_NAME}_my_purchases_link`}
            >
              {myPurchasesPageText}
            </TrackedLink>
          }
        />
      </div>
      <div className="m-t-1">
        <FormattedMessage
          message={_t('For additional information, please see our full {refundPolicyLink}.')}
          refundPolicyLink={
            <TrackedLink
              target="_blank"
              rel="noopener noreferrer"
              href={constants.REFUND_POLICY_LINK}
              trackingName={`${BASE_TRACKING_NAME}_refund_policy_link`}
            >
              {_t('refund policy')}
            </TrackedLink>
          }
        />
      </div>
    </div>
  );
  const getAnswer = () => {
    if (contentfulAnswer) {
      return contentfulAnswer;
    }
    return defaultAnswer;
  };

  return {
    question: _t("Can I try Coursera Plus first, to make sure it's right for me?"),
    answer: getAnswer(),
  };
};

const getTryCourseraPlusFirstForCollectionPage = (explanationForFaq?: RichTextObject | null) => {
  const contentfulAnswer = explanationForFaq ? (
    <div style={{ marginTop: '-15px' }}>
      {/* the arbitrary looking styling takes away the top margin of the first paragraph */}
      <SimpleRichTextContentful richText={explanationForFaq} component={ShowContentfulFaq} />
    </div>
  ) : undefined;

  const getAnswer = () => {
    if (contentfulAnswer) {
      return contentfulAnswer;
    }
    return _t(
      "Yes. We offer a 7-day free trial. Your payment information will be captured when you subscribe, but you won't be charged until the end of your 7-day free trial. If you decide Coursera Plus isn't right for you, just cancel during the free trial period."
    );
  };

  return {
    question: _t('Can I try Coursera Plus before I buy it?'),
    answer: getAnswer(),
  };
};

const getTryCourseraLiteFirst = (explanationForFaq?: RichTextObject | null) => {
  const contentfulAnswer = explanationForFaq ? (
    <div style={{ marginTop: '-15px' }}>
      {/* the arbitrary looking styling takes away the top margin of the first paragraph */}
      <SimpleRichTextContentful richText={explanationForFaq} component={ShowContentfulFaq} />
    </div>
  ) : undefined;

  const defaultAnswer = (
    <div>
      {_t(
        "Yes, we offer a 7-day free trial. We'll capture your payment information when you subscribe, but you won't be charged until the end of your 7-day free trial. If  you decide Coursera Lite isn't right for you, cancel during the trial period and you won't be charged."
      )}
    </div>
  );
  const getAnswer = () => {
    if (contentfulAnswer) {
      return contentfulAnswer;
    }
    return defaultAnswer;
  };

  return {
    question: _t('Can I try Coursera Lite before I purchase a subscription?'),
    answer: getAnswer(),
  };
};

const getSaveMeMoney = () => {
  return {
    question: _t('Will I save money with Coursera Plus?'),
    answer: (
      <div>
        {_t(
          `Yes. If you're taking more than 1 course regularly, you could save up to 30% every month. The more you learn, the more you save.`
        )}
      </div>
    ),
  };
};

const getHowManyCerts = () => {
  return {
    question: _t('How many certificates can I earn with Coursera Plus?'),
    answer: (
      <div>
        {_t(
          `There's no limit. You'll earn a certificate for every course, Project, Specialization, or Professional Certificate you complete.`
        )}
      </div>
    ),
  };
};

const getWhatCanIDoWithCerts = (removeLinks = false) => {
  return {
    question: _t("What can I do with my certificates once I've earned them?"),
    answer: (
      <div>
        <FormattedMessage
          message={_t(
            `You can showcase them on your LinkedIn profile, add them to your {resume}, and share them with your network. You can also share them with your employer to demonstrate your new skills, and your ability to apply those skills on the job.`
          )}
          resume={
            removeLinks ? (
              _t('resume')
            ) : (
              <TrackedLink
                target="_blank"
                rel="noopener noreferrer"
                href={constants.HOW_TO_ADD_RESUME_LINK}
                trackingName={`${BASE_TRACKING_NAME}_my_resume_link`}
              >
                {_t('resume')}
              </TrackedLink>
            )
          }
        />
      </div>
    ),
  };
};

const getWhatIsIncluded = () => {
  const listOfIncludedContentText = _t('this list of included content');
  return {
    question: _t('What is included in Coursera Plus?'),
    answer: (
      <div>
        <FormattedMessage
          message={_t(
            `With your Coursera Plus subscription, you get unlimited access to more than {numberOfProducts} courses, Projects, Specializations, and Professional Certificate programs in a variety of domains, including data science, business, computer science, health, personal development, humanities, and more. The majority of courses on Coursera are included. Certain courses, Specializations, and Professional Certificate programs are excluded. Coursera Plus also does not include degrees or MasterTrack™ Certificate programs. To determine if a particular offering is included, look for the Coursera Plus badge, or check {listOfIncludedContent}.`
          )}
          listOfIncludedContent={
            <TrackedLink
              target="_blank"
              rel="noopener noreferrer"
              href={constants.LIST_OF_INCLUDED_CONTENT}
              trackingName={`${BASE_TRACKING_NAME}_list_of_included_content_link`}
            >
              {listOfIncludedContentText}
            </TrackedLink>
          }
          numberOfProducts={numberOfProductsNoPlusSign}
        />
      </div>
    ),
  };
};

const getWhatIsACollection = () => {
  return {
    question: _t('What is a Computer Science Collection?'),
    answer: _t(
      'This collection is a set of handpicked computer science courses included in a Coursera Plus subscription.'
    ),
  };
};

const getWhatIsIncludedInLite = () => {
  return {
    question: _t("What's included in a Coursera Lite subscription?"),
    answer: _t(
      "When you subscribe, all the content included in your subscription will be marked with a Coursera Lite badge. You'll get  access to videos and lectures in a majority of courses, Specializations, and Professional Certificates. Coursera Lite doesn’t include Degrees, MasterTrack™ Certificates, and some Professional Certificates. Coursera Lite doesn't include graded assessments, quizzes, guided projects, certificates, Degrees, MasterTrack™ Certificates, and some Professional Certificates."
    ),
  };
};

const getMotivationToLearn = () => {
  return {
    question: _t('Will Coursera Plus motivate me to learn?'),
    answer: (
      <div>
        {_t(
          `Based on our data and research, we typically see higher completion rates for Coursera Plus learners. We also hear directly from learners that having a Coursera Plus subscription increases their motivation to learn more.`
        )}
      </div>
    ),
  };
};

const getMotivationToLearnWithLite = () => {
  return {
    question: _t('How can a Coursera Lite subscription improve my motivation to learn?'),
    answer: (
      <div>
        {_t(
          ` Coursera Lite is an economical way to explore a variety of subjects and courses. Exploring different courses in your area of interest can help you discover the right courses. Courses that support your goals can motivate you to learn new skills.`
        )}
      </div>
    ),
  };
};

export const getCourseraPlusPaidMarketingPageFAQs = (
  explanationForFaq?: object | null,
  skipQuestionInFaqAboutFreeTrial?: boolean | null
): FAQContentList => {
  const tryCourseraPlusFirst = skipQuestionInFaqAboutFreeTrial
    ? []
    : [getTryCourseraPlusFirst(explanationForFaq as RichTextObject)];
  const whatIsIncluded = getWhatIsIncluded();
  const saveMeMoney = getSaveMeMoney();
  const howManyCerts = getHowManyCerts();
  const whatCanIDoWithCerts = [getWhatCanIDoWithCerts()];
  const reimbursedByEmployer = getReimbursedByEmployer();
  const existingSubscriptions = getExistingSubscriptions();

  const list: Array<FAQContent> = [
    ...tryCourseraPlusFirst,
    whatIsIncluded,
    saveMeMoney,
    howManyCerts,
    ...whatCanIDoWithCerts,
    reimbursedByEmployer,
    existingSubscriptions,
    getMotivationToLearn(),
  ];
  return list;
};

export const getCourseraLitePaidMarketingPageFAQs = (
  explanationForFaq?: object | null,
  skipQuestionInFaqAboutFreeTrial?: boolean | null
): FAQContentList => {
  const tryCourseraLiteFirst = skipQuestionInFaqAboutFreeTrial
    ? []
    : [getTryCourseraLiteFirst(explanationForFaq as RichTextObject)];
  const whatIsIncluded = getWhatIsIncludedInLite();
  const reimbursedByEmployer = getReimbursedByEmployerForLite();
  const existingSubscriptions = getExistingSubscriptionsForLite();

  const list: Array<FAQContent> = [
    ...tryCourseraLiteFirst,
    whatIsIncluded,
    reimbursedByEmployer,
    existingSubscriptions,
    getMotivationToLearnWithLite(),
  ];
  return list;
};

export const getWhatIsIncludedForCollectionsPage = () => {
  return {
    question: _t("What's included in a Coursera Plus subscription?"),
    answer: (
      <div>
        <FormattedMessage
          message={_t(
            "You'll get access to {numberOfProducts} courses, Projects, Specializations, and Professional Certificate programs in a variety of domains. The majority of Coursera courses are included. Certain courses, Specializations, and Professional Certificate programs are excluded. Coursera Plus doesn't include degrees or MasterTrack™ Certificate programs. To learn if a particular learning program is included, look for the Coursera Plus badge, or check {listOfIncludedContent}."
          )}
          listOfIncludedContent={
            <TrackedLink
              target="_blank"
              rel="noopener noreferrer"
              href={constants.LIST_OF_INCLUDED_CONTENT}
              trackingName={`${BASE_TRACKING_NAME}_list_of_included_content_link`}
            >
              {_t('the included content')}
            </TrackedLink>
          }
          numberOfProducts={numberOfProducts}
        />
      </div>
    ),
  };
};

export const getCanITryPlusForCollectionsPage = () => {
  return {
    question: _t('Can I try Coursera Plus before I buy it?'),
    answer: _t(
      "Yes. We offer a 7-day free trial. Your payment information will be captured when you subscribe, but you won't be charged until the end of your 7-day free trial. If you decide Coursera Plus isn't right for you, just cancel during the free trial period."
    ),
  };
};

// TODO: Refactor this function to return FAQs for Collection landing page once the copy is available
export const getCollectionLandingPageFAQs = (
  explanationForFaq?: object | null,
  skipQuestionInFaqAboutFreeTrial?: boolean | null
): FAQContentList => {
  const tryCourseraPlusFirst = skipQuestionInFaqAboutFreeTrial
    ? []
    : [getTryCourseraPlusFirstForCollectionPage(explanationForFaq as RichTextObject)];
  const list: Array<FAQContent> = [
    getWhatIsACollection(),
    getWhatIsIncludedForCollectionsPage(),
    ...tryCourseraPlusFirst,
    getSaveMeMoney(),
    getHowManyCerts(),
    getWhatCanIDoWithCerts(),
    getReimbursedByEmployer(),
    getExistingSubscriptions(),
    getMotivationToLearn(),
  ];
  return list;
};
